import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useDropzone, FileRejection } from 'react-dropzone';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, ArrowRight, FileSpreadsheet, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
  APPLY_STAFFING_PLAN_IMPORT_MUTATION,
  PREVIEW_STAFFING_PLAN_IMPORT_MUTATION,
} from '../../graphql/queries';
import type { PlanImportPreview } from './types';
import { dayMonth, errorMessage, weekdayShort } from './planningUtils';
import {
  PLAN_IMPORT_MAX_BYTES,
  PLAN_IMPORT_TIMEOUT_MS,
  importCellLabel,
  readFileAsBase64,
} from './planExcel';

interface PlanImportDialogProps {
  open: boolean;
  planId: string;
  onClose: () => void;
  /** Called after a successful import (board is already updated in the cache). */
  onApplied: () => void;
}

/** Runs a mutation with a timeout so the dialog never waits forever. */
function withTimeout() {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), PLAN_IMPORT_TIMEOUT_MS);
  return { context: { fetchOptions: { signal: controller.signal } }, done: () => window.clearTimeout(timer) };
}

function friendlyError(e: unknown, fallback: string): string {
  const err = e as any;
  if (err?.networkError?.name === 'AbortError' || err?.name === 'AbortError') {
    return 'Serwer nie odpowiedział na czas. Plik może być za duży — spróbuj pliku z eksportu tego planu.';
  }
  if (err?.networkError?.statusCode === 413) return 'Plik jest za duży, aby go wysłać.';
  return errorMessage(e, fallback);
}

export const PlanImportDialog: React.FC<PlanImportDialogProps> = ({ open, planId, onClose, onApplied }) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [fileError, setFileError] = useState('');
  const [preview, setPreview] = useState<PlanImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const [previewImport] = useMutation<{ previewStaffingPlanImport: PlanImportPreview }>(
    PREVIEW_STAFFING_PLAN_IMPORT_MUTATION,
  );
  const [applyImport] = useMutation(APPLY_STAFFING_PLAN_IMPORT_MUTATION);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileBase64(null);
      setFileError('');
      setPreview(null);
      setLoading(false);
      setApplying(false);
    }
  }, [open]);

  const runPreview = async (picked: File) => {
    setFile(picked);
    setPreview(null);
    setFileError('');
    setFileBase64(null);
    if (picked.size > PLAN_IMPORT_MAX_BYTES) {
      setFileError('Plik jest za duży (limit 2 MB).');
      return;
    }
    setLoading(true);
    const t = withTimeout();
    try {
      const base64 = await readFileAsBase64(picked);
      const { data } = await previewImport({ variables: { planId, fileBase64: base64 }, context: t.context });
      setFileBase64(base64);
      setPreview(data?.previewStaffingPlanImport ?? null);
    } catch (e) {
      setFileError(friendlyError(e, 'Nie udało się sprawdzić pliku'));
    } finally {
      t.done();
      setLoading(false);
    }
  };

  const onDrop = (accepted: File[], rejected: FileRejection[]) => {
    if (rejected.length > 0 || accepted.length === 0) {
      setFile(null);
      setPreview(null);
      setFileError('Wybierz jeden plik Excela w formacie .xlsx.');
      return;
    }
    runPreview(accepted[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    maxFiles: 1,
    multiple: false,
    disabled: loading || applying,
  });

  const changesByEmployee = useMemo(() => {
    const groups: Array<{ employeeId: string; name: string; items: PlanImportPreview['changes'] }> = [];
    const byId = new Map<string, (typeof groups)[number]>();
    for (const c of preview?.changes ?? []) {
      let g = byId.get(c.employeeId);
      if (!g) {
        g = { employeeId: c.employeeId, name: c.employeeName, items: [] };
        byId.set(c.employeeId, g);
        groups.push(g);
      }
      g.items.push(c);
    }
    groups.forEach((g) => g.items.sort((a, b) => a.date.localeCompare(b.date)));
    return groups;
  }, [preview]);

  const hardAfter = preview?.violations.filter((v) => v.severity === 'HARD') ?? [];
  const softAfter = preview?.violations.filter((v) => v.severity !== 'HARD') ?? [];
  const hasFileErrors = (preview?.errors.length ?? 0) > 0;
  const noChanges = !!preview && preview.changes.length === 0;
  const canApply = !!preview && !!fileBase64 && !hasFileErrors && !noChanges && !applying && !loading;

  const handleApply = async () => {
    if (!canApply) return;
    setApplying(true);
    const t = withTimeout();
    try {
      await applyImport({ variables: { planId, fileBase64 }, context: t.context });
      toast.success(`Zaimportowano zmiany (${preview!.changes.length})`);
      onApplied();
      onClose();
    } catch (e) {
      toast.error(friendlyError(e, 'Nie udało się zaimportować pliku'));
    } finally {
      t.done();
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !applying && onClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] flex flex-col p-0" data-testid="plan-import-dialog">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Importuj plan z Excela</DialogTitle>
          <DialogDescription>
            Wgraj plik wyeksportowany z tego planu i poprawiony w Excelu. Zanim cokolwiek zapiszesz, zobaczysz listę
            zmian. Zaimportowane komórki zostaną przypięte — automatyczne układanie ich nie zmieni.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          <div
            {...getRootProps()}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors',
              isDragActive ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50',
              (loading || applying) && 'cursor-not-allowed opacity-60',
            )}
            data-testid="plan-import-dropzone"
          >
            <input {...getInputProps()} data-testid="plan-import-input" />
            {file ? (
              <>
                <FileSpreadsheet className="h-8 w-8 text-primary" />
                <span className="font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">Kliknij lub upuść inny plik, aby go zamienić</span>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span>{isDragActive ? 'Upuść plik tutaj' : 'Przeciągnij plik .xlsx tutaj albo kliknij, aby wybrać'}</span>
                <span className="text-xs text-muted-foreground">Tylko pliki .xlsx, do 2 MB</span>
              </>
            )}
          </div>

          {fileError && (
            <p className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400" data-testid="plan-import-file-error">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {fileError}
            </p>
          )}
          {loading && <p className="text-sm text-muted-foreground">Sprawdzanie pliku...</p>}

          {preview && (
            <div className="space-y-4" data-testid="plan-import-preview">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                <span>
                  Zmiany: <strong data-testid="plan-import-change-count">{preview.changes.length}</strong>
                </span>
                <span>
                  Bez zmian: <strong>{preview.unchangedCount}</strong>
                </span>
                <span className={cn(hasFileErrors && 'text-red-600 dark:text-red-400')}>
                  Błędy w pliku: <strong>{preview.errors.length}</strong>
                </span>
              </div>

              {hasFileErrors && (
                <div className="rounded-md border border-red-300 dark:border-red-900">
                  <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
                    Błędy w pliku — popraw je w Excelu i wgraj plik ponownie
                  </div>
                  <ul className="max-h-40 divide-y overflow-y-auto text-sm">
                    {preview.errors.map((e, i) => (
                      <li key={i} className="px-3 py-1.5">
                        <span className="font-medium">
                          Wiersz {e.row}
                          {e.column ? `, kolumna ${e.column}` : ''}:
                        </span>{' '}
                        {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {noChanges && !hasFileErrors && (
                <p className="rounded-md border bg-muted/50 px-3 py-2 text-sm" data-testid="plan-import-no-changes">
                  Plik nie wprowadza żadnych zmian — wszystko jest takie jak w planie.
                </p>
              )}

              {changesByEmployee.length > 0 && (
                <div className="rounded-md border">
                  <div className="border-b bg-muted/50 px-3 py-2 text-sm font-medium">Co się zmieni (było → będzie)</div>
                  <ul className="max-h-64 divide-y overflow-y-auto text-sm">
                    {changesByEmployee.map((g) => (
                      <li key={g.employeeId} className="px-3 py-2">
                        <div className="font-medium">{g.name}</div>
                        <ul className="mt-1 space-y-0.5">
                          {g.items.map((c) => (
                            <li key={c.date} className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
                              <span className="w-16 shrink-0 text-foreground">
                                {weekdayShort(c.date)} {dayMonth(c.date)}
                              </span>
                              <span>{importCellLabel(c.from)}</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                              <span className="font-medium text-foreground">{importCellLabel(c.to)}</span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(hardAfter.length > 0 || softAfter.length > 0) && !noChanges && (
                <div className="rounded-md border">
                  <div className="border-b bg-muted/50 px-3 py-2 text-sm font-medium">
                    Problemy w planie po imporcie — błędy: {hardAfter.length}, ostrzeżenia: {softAfter.length}
                  </div>
                  <ul className="max-h-40 divide-y overflow-y-auto text-sm">
                    {[...hardAfter, ...softAfter].map((v, i) => (
                      <li key={i} className="flex items-start gap-2 px-3 py-1.5">
                        {v.severity === 'HARD' ? (
                          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                        ) : (
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        )}
                        {v.message}
                      </li>
                    ))}
                  </ul>
                  {hardAfter.length > 0 && (
                    <p className="border-t px-3 py-2 text-xs text-muted-foreground">
                      Import jest możliwy, ale błędy trzeba będzie poprawić przed publikacją planu.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 border-t px-6 pb-6 pt-4">
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Anuluj
          </Button>
          <Button onClick={handleApply} disabled={!canApply} data-testid="plan-import-apply">
            {applying ? 'Importowanie...' : 'Zastosuj zmiany'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
